import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoJsPlayerProps {
  videoUrl: string;
  signingParams?: Record<string, string> | null;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  initialTime?: number;
  className?: string;
}

const VideoJsPlayer: React.FC<VideoJsPlayerProps> = ({
  videoUrl,
  signingParams,
  onEnded,
  onTimeUpdate,
  initialTime,
  className = ''
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [qualities, setQualities] = useState<string[]>([]);
  const hasSeekedinitiallyRef = useRef(false);
  const initialTimeRef = useRef(initialTime);

  // Keep ref updated and seek when initialTime arrives
  useEffect(() => {
    initialTimeRef.current = initialTime;
    const player = playerRef.current;
    if (player && !player.isDisposed() && initialTime && initialTime > 0 && !hasSeekedinitiallyRef.current) {
      hasSeekedinitiallyRef.current = true;
      console.log("Seeking to:", initialTime);
      player.currentTime(initialTime);
    }
  }, [initialTime]);
  const [selectedQuality, setSelectedQuality] = useState<string>('Auto');

  useEffect(() => {
    // Initialize player only once
    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('vjs-big-play-centered');
      videoRef.current.appendChild(videoElement);

      const player = videojs(videoElement, {
        controls: true,
        responsive: true,
        fluid: true,
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        controlBar: {
          volumePanel: { inline: false },
          pictureInPictureToggle: false
        },
        html5: {
          vhs: {
            overrideNative: true,
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            maxBufferSize: 60 * 1000 * 1000,
            maxBufferHole: 0.5
          }
        }
      });

      playerRef.current = player;

      // Set source
      player.src({
        src: videoUrl,
        type: 'application/x-mpegURL'
      });

      // Append CloudFront signing params to every HLS request (global hook)
      // Use latest signingParams via window to avoid stale closure when navigating between lessons
      if (signingParams) {
        (window as any).__cfSigningParams = signingParams;
        (videojs as any).Vhs.xhr.beforeRequest = function(options: any) {
          const params = (window as any).__cfSigningParams;
          if (params && options.uri && (options.uri.includes('cdn.itilshift.com') || options.uri.includes('cloudfront.net'))) {
            const separator = options.uri.includes('?') ? '&' : '?';
            options.uri += separator + 'Policy=' + params.Policy + '&Signature=' + params.Signature + '&Key-Pair-Id=' + params['Key-Pair-Id'];
          }
          return options;
        };
      }

      // Detect quality levels and seek to saved position
      player.one('loadedmetadata', () => {
        // Seek to saved position
        const seekTo = initialTimeRef.current;
        if (seekTo && seekTo > 0) {
          player.currentTime(seekTo);
        }
        const qualityLevels = player.qualityLevels();
        if (qualityLevels && qualityLevels.length > 0) {
          const levels = ['Auto'];
          for (let i = 0; i < qualityLevels.length; i++) {
            levels.push(`${qualityLevels[i].height}p`);
          }
          setQualities(levels);
        }


      });

      // Ended event
      if (onEnded) {
        player.on('ended', onEnded);
      }

      // Time update event (fires every second)
      if (onTimeUpdate) {
        player.on('timeupdate', () => {
          const currentTime = player.currentTime();
          const duration = player.duration();
          if (duration > 0) {
            onTimeUpdate(currentTime, duration);
          }
        });
      }
    }

    // Cleanup on unmount only
    return () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);


  const handleQualityChange = (quality: string) => {
    setSelectedQuality(quality);
    const player = playerRef.current;
    if (!player) return;

    const qualityLevels = player.qualityLevels();
    if (!qualityLevels) return;

    if (quality === 'Auto') {
      // Enable all qualities for auto-switching
      for (let i = 0; i < qualityLevels.length; i++) {
        qualityLevels[i].enabled = true;
      }
    } else {
      // Disable all except selected quality
      const targetHeight = parseInt(quality);
      for (let i = 0; i < qualityLevels.length; i++) {
        qualityLevels[i].enabled = qualityLevels[i].height === targetHeight;
      }
    }
  };

  return (
    <div className="relative">
      <div data-vjs-player className={className}>
        <div ref={videoRef} />
      </div>
      
      {/* Manual Quality Selector */}
      {qualities.length > 1 && (
        <div className="absolute top-4 right-4 z-50">
          <select
            value={selectedQuality}
            onChange={(e) => handleQualityChange(e.target.value)}
            className="bg-black/70 text-white px-3 py-2 rounded-lg text-sm border border-white/20 cursor-pointer hover:bg-black/90 transition-colors"
          >
            {qualities.map((quality) => (
              <option key={quality} value={quality}>
                {quality}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default VideoJsPlayer;
